import { useMemo } from 'react';
import { SectionHeader } from '@/components/molecules/SectionHeader';
import type { Character, MoveRef } from '@/schemas/character';
import { isAutoSpellGrantLevelRule } from '@/schemas/move';
import {
  type ActiveSpellGrant,
  getActiveSpellGrantsForLevelUpMany,
  getDisplayableSpellGrantsForLevelUpMany,
  getGrantKey,
} from '@/services/spellGrants';
import { getActiveSpellLists, type ActiveSpellList } from '@/services/spellAccess';
import { buildJustAcquiredMoves } from './justAcquired';
import {
  AutoGrantInfoSection,
  InteractiveGrantSection,
  TraditionInfoSection,
} from './LevelUpStepSpellsParts';
import type { LevelUpGrantedChoiceDraft, LevelUpMulticlassPickDraft } from './types';

// page-specific (Fase 2 PR 10): step «Incantesimi» del flusso di salita
// di livello (D2/FI-33). Pixel-parity con pattern shell PR 8-9: section
// + SectionHeader, no bottoni interni (delegati a `<StepNav>` esterno).
//
// 2 sezioni complementari:
//  - **Interattive** (`<InteractiveGrantSection>`): per ogni grant attiva
//    (Mago Corpus Arcano ricorrente; mosse advanced con `trigger: 'on-take'`
//    come Magnus, Tomo del Sapere, Rivelazioni Proibite), counter + cap
//    perLevelCap.
//  - **Informative** (`<TraditionInfoSection>`): per casteggianti
//    tradizione-native (Chierico/Druido-Natura/Paladino-Sacro), lista
//    read-only spell auto + badge "Nuovo a L{N}" per gli appena sbloccati.
//
// Sub-componenti `<InteractiveGrantSection>` + `<TraditionInfoSection>`
// + helper descrittivi vivono in `LevelUpStepSpellsParts.tsx` (decisione
// B1 — file principale sotto `max-lines: 400`).
//
// **PR 10 cambio API**: `onNext` / `onBack` rimosse. La completion check
// `areAllSpellGrantsSelectionsValid` è ora chiamata da `LevelUpFlowPage`
// per pilotare lo `<StepNav>` esterno.

export interface LevelUpStepSpellsProps {
  character: Character;
  targetLevel: number;
  moveAdded: MoveRef | undefined;
  grantedChoice?: LevelUpGrantedChoiceDraft | undefined;
  multiclassPick?: LevelUpMulticlassPickDraft | undefined;
  selections: Readonly<Record<string, readonly string[]>>;
  onSelectionsChange: (next: Record<string, readonly string[]>) => void;
}

export function LevelUpStepSpells({
  character,
  targetLevel,
  moveAdded,
  grantedChoice,
  multiclassPick,
  selections,
  onSelectionsChange,
}: LevelUpStepSpellsProps) {
  const justAcquired = useMemo<readonly MoveRef[]>(
    () => buildJustAcquiredMoves(character.classId, moveAdded, grantedChoice, multiclassPick),
    [character.classId, moveAdded, grantedChoice, multiclassPick],
  );

  const activeGrants = useMemo(
    () => getActiveSpellGrantsForLevelUpMany(character.classId, character.moves, justAcquired),
    [character.classId, character.moves, justAcquired],
  );

  // **FI-56**: le grant "auto" (`all-of-level`/`fixed-list`) sulle mosse
  // appena acquisite via multiclass-bundle (es. trucchetti Mago auto da LIA
  // pescata via Combattente Multiclasse) vanno mostrate al giocatore in
  // modalità read-only. `getDisplayableSpellGrantsForLevelUpMany` ritorna sia
  // pick sia auto; filtriamo le auto su justAcquired per non duplicare le
  // pick (già coperte da `activeGrants`).
  const autoGrants = useMemo<readonly ActiveSpellGrant[]>(
    () =>
      getDisplayableSpellGrantsForLevelUpMany(character.classId, [], justAcquired).filter((g) =>
        isAutoSpellGrantLevelRule(g.grant.levelRule),
      ),
    [character.classId, justAcquired],
  );

  // FI-35 Gap E: per le tradition-list info, includi le mosse appena
  // acquisite (es. Vincolo del Tomo via grantedChoice attiva warlock-native
  // anche se non era ancora in character.moves)
  const traditionLists = useMemo(
    () => collectTraditionLists([...character.moves, ...justAcquired]),
    [character.moves, justAcquired],
  );

  const isEmpty =
    activeGrants.length === 0 && autoGrants.length === 0 && traditionLists.length === 0;

  return (
    <section>
      <SectionHeader
        title="Incantesimi"
        sub={`Scelte e disponibilità degli incantesimi al livello ${targetLevel}.`}
      />
      <div className="mt-4 space-y-6">
        {isEmpty ? (
          <p className="text-ink-soft font-serif text-[0.92rem] italic">
            Nessuna nuova scelta di incantesimi a questo livello.
          </p>
        ) : null}
        {autoGrants.map((ag) => (
          <AutoGrantInfoSection
            key={`auto-${getGrantKey(ag)}`}
            grant={ag}
            character={character}
            targetLevel={targetLevel}
          />
        ))}
        {activeGrants.map((ag) => (
          <InteractiveGrantSection
            key={getGrantKey(ag)}
            grant={ag}
            character={character}
            targetLevel={targetLevel}
            selectedIds={selections[getGrantKey(ag)] ?? []}
            offLimitsIds={collectOffLimitsForSection(character, selections, getGrantKey(ag))}
            onChange={(spellIds) =>
              onSelectionsChange({ ...selections, [getGrantKey(ag)]: spellIds })
            }
          />
        ))}
        {traditionLists.map((list) => (
          <TraditionInfoSection
            key={traditionKey(list)}
            list={list}
            characterLevel={character.level}
            targetLevel={targetLevel}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Spell ID che il pool della sezione `currentGrantKey` deve escludere:
 * spell preesistenti + selezioni nelle ALTRE sezioni del medesimo level-up.
 * La sezione corrente NON viene esclusa, così gli spell appena selezionati
 * restano visibili con checkbox checked (l'utente può deselezionarli).
 */
function collectOffLimitsForSection(
  character: Character,
  selections: Readonly<Record<string, readonly string[]>>,
  currentGrantKey: string,
): ReadonlySet<string> {
  const ids = new Set<string>(character.spells.map((s) => s.id));
  for (const [key, list] of Object.entries(selections)) {
    if (key === currentGrantKey) continue;
    for (const id of list) ids.add(id);
  }
  return ids;
}

function collectTraditionLists(moves: readonly MoveRef[]): readonly ActiveSpellList[] {
  return getActiveSpellLists(moves).filter(
    (list) =>
      list.mode === 'chierico-native' ||
      list.mode === 'druido-native' ||
      list.mode === 'paladino-native',
  );
}

function traditionKey(list: ActiveSpellList): string {
  if (list.classId === 'chierico' || list.classId === 'paladino') {
    return `${list.classId}-${list.tradizione}`;
  }
  return list.classId;
}
